import {SPACE_META_KEY, type PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import {chatReducer, type ChatState, type ChatTurnCancelledAction} from '@microsoft/agent-host-protocol'
import {useCallback, useMemo, useRef, useState} from 'react'

interface OptimisticRecoveryTurn {
  readonly id: string
  readonly prompt: PreparedPrompt
  readonly cancelled: boolean
  readonly startedAt?: string
  readonly withdrawOnCanonicalUserTurn?: boolean
}

export const EXTERNAL_OPTIMISTIC_TURN_ID = 'pending-initial-submission-host'

function hasCanonicalTurn(state: ChatState | undefined, optimisticTurn: OptimisticRecoveryTurn): boolean {
  if (!state) return false
  const turns = [...(state.turns ?? []), ...(state.activeTurn ? [state.activeTurn] : [])]
  return turns.some(
    turn =>
      turn.message?.origin?.kind === 'user' &&
      (optimisticTurn.withdrawOnCanonicalUserTurn || turn.message.text === optimisticTurn.prompt.text),
  )
}

function optimisticRecoveryState(
  providerState: ChatState | undefined,
  optimisticTurns: readonly OptimisticRecoveryTurn[],
): ChatState | undefined {
  if (optimisticTurns.length === 0) return providerState
  if (providerState && optimisticTurns.every(turn => hasCanonicalTurn(providerState, turn))) {
    return providerState
  }
  let state = {...(providerState ?? ({status: 1, turns: []} as unknown as ChatState))}
  for (const turn of optimisticTurns) {
    if (hasCanonicalTurn(providerState, turn)) continue
    state = {
      ...state,
      status: 8,
      turns: state.turns ?? [],
      activeTurn: {
        id: turn.id,
        startedAt: turn.startedAt ?? state.modifiedAt,
        message: {
          text: turn.prompt.text,
          origin: {kind: 'user'},
          ...(turn.prompt.attachments ? {attachments: turn.prompt.attachments} : {}),
          ...(turn.prompt.displaySpace
            ? {
                _meta: {
                  [SPACE_META_KEY]: {
                    owner: turn.prompt.displaySpace.owner,
                    id: turn.prompt.displaySpace.id,
                    slug: turn.prompt.displaySpace.slug,
                    name: turn.prompt.displaySpace.name,
                  },
                },
              }
            : {}),
        },
        responseParts: [],
        usage: undefined,
      },
    } as unknown as ChatState
    if (turn.cancelled) {
      state = chatReducer(state, {
        type: 'chat/turnCancelled',
        turnId: turn.id,
        duration: 0,
      } as ChatTurnCancelledAction)
    }
  }
  return state
}

/** Adds an externally owned optimistic prompt to canonical AHP state. */
export function withOptimisticRecoveryTurn(
  providerState: ChatState | undefined,
  prompt: PreparedPrompt | undefined,
  id = EXTERNAL_OPTIMISTIC_TURN_ID,
  withdrawOnCanonicalUserTurn = false,
): ChatState | undefined {
  return optimisticRecoveryState(
    providerState,
    prompt ? [{id, prompt, cancelled: false, withdrawOnCanonicalUserTurn}] : [],
  )
}

/**
 * Optimistic first turns shown while the session behind them provisions.
 *
 * The externally owned prompt appears and clears atomically with its prop.
 * Locally started turns retain stable identities for stop/retry transitions.
 */
export function useOptimisticRecoveryTurns(providerState: ChatState | undefined, externalPrompt?: PreparedPrompt) {
  const [turns, setTurns] = useState<OptimisticRecoveryTurn[]>([])
  const sequenceRef = useRef(0)
  const effectiveTurns = useMemo(
    () =>
      turns.length > 0 || !externalPrompt
        ? turns
        : [{id: EXTERNAL_OPTIMISTIC_TURN_ID, prompt: externalPrompt, cancelled: false}],
    [externalPrompt, turns],
  )
  const state = useMemo(() => optimisticRecoveryState(providerState, effectiveTurns), [providerState, effectiveTurns])
  const onStarted = useCallback((prompt: PreparedPrompt) => {
    const id = `pending-initial-submission-${++sequenceRef.current}`
    const startedAt = new Date().toISOString()
    setTurns(current => {
      const activeTurnIndex = current.findLastIndex(turn => !turn.cancelled)
      if (activeTurnIndex >= 0) {
        return current.map((turn, index) => (index === activeTurnIndex ? {...turn, prompt} : turn))
      }
      return [...current, {id, prompt, cancelled: false, startedAt}]
    })
  }, [])
  const onStopped = useCallback(() => {
    setTurns(current => {
      const activeTurnIndex = current.findLastIndex(turn => !turn.cancelled)
      if (activeTurnIndex < 0) return current
      return current.map((turn, index) => (index === activeTurnIndex ? {...turn, cancelled: true} : turn))
    })
  }, [])
  const clear = useCallback(() => setTurns([]), [])

  return {
    state,
    hasTurns: effectiveTurns.length > 0,
    hasActiveTurn: effectiveTurns.some(turn => !turn.cancelled),
    turnCount: effectiveTurns.length,
    onStarted,
    onStopped,
    clear,
  }
}
